import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { apiService } from "@/api";
import type { AccountUserTree, AccountUserWithAccount } from "@/api/queries/account";
import { withOperationHandlingAtom } from "../operations";
import { selectedPropertyAtom, virtualDeviceConfigurationAtom } from "./property-domain";

// Debouncing configuration
export const ACCOUNT_USER_TREE_DEBOUNCE_DELAY = 500; // 500ms

export const availableAccountsAtom = atom<AccountUserWithAccount[]>([]);
availableAccountsAtom.debugLabel = "availableAccountsAtom";

const internalSelectedAccountUserIdAtom = atomWithStorage<string | null>("selectedAccountUserId", null, undefined, {
  getOnInit: true, // Load from storage on initialization
});
internalSelectedAccountUserIdAtom.debugLabel = "internalSelectedAccountUserIdAtom";

export const selectedAccountUserIdAtom = atom((get) => {
  const id = get(internalSelectedAccountUserIdAtom);
  if (!id) {
    console.warn("No account user ID selected");
  }
  return id;
});
selectedAccountUserIdAtom.debugLabel = "selectedAccountUserIdAtom";

export const accountUserTreeAtom = atom<AccountUserTree | null>(null);
accountUserTreeAtom.debugLabel = "accountUserTreeAtom";

export const fetchAccountsAtom = atom(null, async (get, set) => {
  const result = await set(
    withOperationHandlingAtom,
    {
      operation: "fetchAccounts",
      message: "Carregando contas...",
      displayLoading: true,
      displayError: true,
    },
    async () => {
      const accounts = await apiService.account.listAccountsWithUsers();
      set(availableAccountsAtom, accounts);
      return accounts;
    }
  );

  return result;
});
fetchAccountsAtom.debugLabel = "fetchAccountsAtom";

let fetchAccountUserTreeTimeout: NodeJS.Timeout | null = null;
let pendingAccountUserId: string | null = null;

const clearFetchAccountUserTreeTimeout = () => {
  if (fetchAccountUserTreeTimeout) {
    clearTimeout(fetchAccountUserTreeTimeout);
    fetchAccountUserTreeTimeout = null;
  }
  pendingAccountUserId = null;
};

export const fetchSelectedAccountUserTreeAtom = atom(null, async (get, set) => {
  const targetId = get(internalSelectedAccountUserIdAtom);
  if (!targetId) {
    console.log("fetchSelectedAccountUserTreeAtom - No account user ID provided");
    throw new Error("No account user ID provided");
  }

  console.log(
    `fetchSelectedAccountUserTreeAtom - Requesting data for account ${targetId} (debounce: ${ACCOUNT_USER_TREE_DEBOUNCE_DELAY}ms)`
  );

  if (pendingAccountUserId === targetId && fetchAccountUserTreeTimeout) {
    console.log(`fetchSelectedAccountUserTreeAtom - Request for account ${targetId} already pending, skipping`);
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!fetchAccountUserTreeTimeout || pendingAccountUserId !== targetId) {
          clearInterval(checkInterval);
          resolve({ success: true });
        }
      }, 50);
    });
  }

  clearFetchAccountUserTreeTimeout();
  pendingAccountUserId = targetId;

  return new Promise((resolve, reject) => {
    fetchAccountUserTreeTimeout = setTimeout(async () => {
      try {
        const currentTargetId = get(internalSelectedAccountUserIdAtom);
        if (currentTargetId !== targetId) {
          console.log(
            `fetchSelectedAccountUserTreeAtom - Account ID changed during debounce (${targetId} -> ${currentTargetId}), cancelling request`
          );
          clearFetchAccountUserTreeTimeout();
          resolve({ success: false, reason: "Account ID changed" });
          return;
        }

        console.log(`fetchSelectedAccountUserTreeAtom - Executing debounced request for account ${targetId}`);

        const result = await set(
          withOperationHandlingAtom,
          {
            operation: "fetchAccountUserTree",
            message: "Carregando dados da conta...",
            displayLoading: true,
            displayError: true,
          },
          async () => {
            const tree = await apiService.account.getAccountUserTree(targetId);
            tree.account.properties.sort((a, b) => a.name.localeCompare(b.name));
            tree.account.properties.forEach((property) => {
              property.projects.sort((a, b) => a.name.localeCompare(b.name));
              property.projects.forEach((project) => {
                const collator = new Intl.Collator(undefined, {
                  numeric: true,
                  sensitivity: "base",
                });
                project.sectors.sort((a, b) => collator.compare(a.name, b.name));
              });
            });
            set(accountUserTreeAtom, tree);
            await set(virtualDeviceConfigurationAtom);
            console.log(`fetchSelectedAccountUserTreeAtom - Successfully loaded data for account ${targetId}`);
            Object.assign(window, {
              accountUserTree: tree,
            });

            return tree;
          }
        );

        clearFetchAccountUserTreeTimeout();
        resolve(result);
      } catch (error) {
        clearFetchAccountUserTreeTimeout();
        reject(error);
      }
    }, ACCOUNT_USER_TREE_DEBOUNCE_DELAY);
  });
});
fetchSelectedAccountUserTreeAtom.debugLabel = "fetchSelectedAccountUserTreeAtom";

export const assignAccountAtom = atom(
  null,
  async (
    get,
    set,
    {
      accountUserId,
      navigate,
      nextLocation = "/app/dashboard",
    }: {
      accountUserId: string;
      nextLocation?: string;
      navigate: (path: string) => void;
    }
  ) => {
    try {
      console.log("🔄 assignAccountAtom - Assigning current account:", accountUserId);

      set(internalSelectedAccountUserIdAtom, accountUserId);

      await set(fetchSelectedAccountUserTreeAtom);

      console.log("✅ assignAccountAtom - Account data loaded successfully - redirecting to app");

      const selectedProperty = get(selectedPropertyAtom);

      if (selectedProperty) {
        console.log(
          `🔄 assignAccountAtom - Property already selected: ${selectedProperty.id}, navigating to dashboard ${nextLocation}`
        );

        navigate("/");
        return { success: true };
      }

      console.log("🔄 No property selected - redirecting to select property");
      navigate("/select-property");

      return { success: true };
    } catch (error) {
      console.error("❌ Failed to load account data:", error);
      navigate("/select-account");
      throw error;
    }
  }
);
assignAccountAtom.debugLabel = "assignAccountAtom";

export const unassignAccountAtom = atom(null, async (get, set) => {
  console.log("🔄 Unassigning current account");
  clearFetchAccountUserTreeTimeout();
  set(internalSelectedAccountUserIdAtom, null);
  set(accountUserTreeAtom, null);
  const { selectedPropertyIdAtom } = await import("./property-domain");
  set(selectedPropertyIdAtom, null);
});
unassignAccountAtom.debugLabel = "unassignAccountAtom";

export const clearPendingAccountUserTreeRequests = () => {
  clearFetchAccountUserTreeTimeout();
  console.log("🧹 Manually cleared pending account user tree requests");
};
